/**
 * Global chrome: brand, filters, window, search, theme (F1–F3, F16, F17).
 */
import type { BookFilterParam, FilterKey, WindowKey } from '@meridian/contracts';

import styles from './app-header.module.css';
import { BookFilter } from './book-filter';
import { ThemeToggle } from './theme-toggle';
import { TickerSearch } from './ticker-search';
import { UniverseFilter } from './universe-filter';
import { WindowToggle } from './window-toggle';

import type { JSX, ReactNode } from 'react';

export interface AppHeaderProps {
  readonly index: FilterKey;
  readonly window: WindowKey;
  readonly onIndexChange: (v: FilterKey) => void;
  readonly onWindowChange: (v: WindowKey) => void;
  /**
   * Manual-book filter (ADR-0042). REQUIRED, not optional: `AppShell` is the
   * only call site and always has the book, so an optional pair would add a
   * branch the product never takes and coverage could only reach through a
   * test written for the branch's sake.
   */
  readonly book: BookFilterParam | undefined;
  readonly onBookChange: (v: BookFilterParam | undefined) => void;
  readonly bookLotCount: number;
  /**
   * Optional account control, rendered at the trailing edge of the controls row.
   *
   * A slot rather than a direct `<AccountMenu />` because it inverts the
   * dependency: the header never imports `useAuth`, so its tests need no
   * `AuthProvider`. The shell omits the prop on `/login` and while
   * unauthenticated, so this wrapper is not mounted as an empty box.
   */
  readonly account?: ReactNode;
}

export function AppHeader({
  index,
  window,
  onIndexChange,
  onWindowChange,
  account,
  book,
  onBookChange,
  bookLotCount,
}: AppHeaderProps): JSX.Element {
  return (
    <header className={styles['header']}>
      <div className={styles['brand']}>
        <div className={styles['dot']} aria-hidden="true" />
        <div className={styles['wordmark']}>MERIDIAN</div>
        <div className={styles['tag']}>EQUITY SCREEN</div>
      </div>
      <div className={styles['controls']}>
        <UniverseFilter value={index} onChange={onIndexChange} />
        <BookFilter value={book} onChange={onBookChange} lotCount={bookLotCount} />
        <WindowToggle value={window} onChange={onWindowChange} />
        <TickerSearch />
        <ThemeToggle />
        {account !== undefined ? <div className={styles['account']}>{account}</div> : null}
      </div>
    </header>
  );
}
